import { Stack, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import RequiredFieldMarker from "@entrilia/shared/components/RequiredFieldMarker";
import RoundedTable from "@entrilia/shared/components/RoundedTable";
import { convertToNumber } from "@entrilia/shared/utilities/numberHelper";
import { autoFormatCamelCase, stringifyValue } from "@entrilia/shared/utilities/stringHelper";
import type { WorkflowActivityChangeSetField } from "../../../../../api/types/workflowTypes";
import EntityValueGridCell from "../../../../entityFields/EntityValueGridCell";
import MultiLookupField from "../../../../entityFields/editors/MultiLookupField";
import ConfidenceIndicator from "./ConfidenceIndicator";
import InstructionSourceInfoIcon from "./InstructionSourceInfoIcon";
import WorkflowFieldCitationButton from "./WorkflowFieldCitationButton";

interface BodyRowProps {
  field: WorkflowActivityChangeSetField;
  showSourceColumn?: boolean;
}

const BodyRow = ({ field, showSourceColumn }: BodyRowProps) => {
  const hasValue = field.newValue !== null && field.newValue !== undefined;
  const confidence = convertToNumber(field.metadata?.Confidence);
  const source = stringifyValue(field.metadata?.Source);
  const sourceDocumentName = stringifyValue(field.metadata?.SourceDocument);
  const isRequired = field.attributes?.includes("Required");

  return (
    <TableRow>
      <TableCell width="20%" sx={(t) => ({ minWidth: 150, color: t.palette.text.secondary })}>
        {field.caption}
        {isRequired && <RequiredFieldMarker />}
      </TableCell>
      <TableCell width="65%">
        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
          {field.fieldType === "MultiLookup" && field.fieldConfiguration?.$type === "Lookup" ? (
            <MultiLookupField
              objectType={field.fieldConfiguration.objectType}
              value={field.newValue}
              label={field.caption}
              readOnly
            />
          ) : (
            hasValue && (
              <EntityValueGridCell
                type={field.fieldType}
                value={field.newValue}
                configuration={field.fieldConfiguration}
                wrapTextValue
              />
            )
          )}
          {hasValue && confidence !== undefined && <ConfidenceIndicator confidence={confidence} />}
        </Stack>
      </TableCell>
      {showSourceColumn && (
        <TableCell width="15%" sx={{ minWidth: 100 }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <WorkflowFieldCitationButton fieldName={field.name} tooltip={sourceDocumentName} />
            <Typography color="textSecondary">{autoFormatCamelCase(source)}</Typography>
            <InstructionSourceInfoIcon field={field} />
          </Stack>
        </TableCell>
      )}
    </TableRow>
  );
};

interface Props {
  fields: WorkflowActivityChangeSetField[];
  showSourceColumn?: boolean;
}

const WorkflowActivityChangeSetDiffTable = ({ fields, showSourceColumn }: Props) => {
  return (
    <RoundedTable>
      <TableHead>
        <TableRow>
          <TableCell>Field</TableCell>
          <TableCell>Value</TableCell>
          {showSourceColumn && <TableCell>Source</TableCell>}
        </TableRow>
      </TableHead>
      <TableBody>
        {fields.map((field) => (
          <BodyRow key={field.name} field={field} showSourceColumn={showSourceColumn} />
        ))}
      </TableBody>
    </RoundedTable>
  );
};

export default WorkflowActivityChangeSetDiffTable;
